import { logInfo } from 'setup/logger/logInfo';
import { checkForServiceWorkerUpdate } from '../requestServiceWorkerUpdate';
import { getServiceWorkerContainer } from '../serviceWorkerContainer';
import { readEntryScriptFromHtml, readEntryScriptOfPage } from './entryScript';
import { createDeployedVersionWatch, DEPLOYED_VERSION_CHECK_INTERVAL_MS } from './model';

const INDEX_HTML_PATH = '/index.html';

function isDocumentVisible(): boolean {
  return document.visibilityState === 'visible';
}

async function fetchDeployedEntryScript(): Promise<string | null> {
  try {
    // The worker caches no index.html today; the unique address keeps a future precache (matched by URL) from answering.
    const response = await fetch(`${INDEX_HTML_PATH}?t=${Date.now()}`, { cache: 'no-store' });
    return response.ok ? readEntryScriptFromHtml(await response.text()) : null;
  } catch {
    // Offline: the next check tries again.
    return null;
  }
}

/**
 * A tab kept open across a deploy keeps its version: the browser looks for a
 * new worker only on navigations, which this app does not make (PT-7331).
 * Every hour, and on the return to the tab, the entry script named by the
 * server's `index.html` is read; another one than the page runs starts the
 * worker update, so the update banner shows.
 * Returns the stop of the watch.
 */
export function watchDeployedVersion(): () => void {
  const pageEntryScript = readEntryScriptOfPage();
  if (pageEntryScript === null || !getServiceWorkerContainer()) {
    return () => undefined;
  }

  const watch = createDeployedVersionWatch({
    pageEntryScript,
    readDeployedEntryScript: fetchDeployedEntryScript,
    onOtherVersionDeployed: (deployedEntryScript) => {
      logInfo('deployed-version-detected', { pageEntryScript, deployedEntryScript });
      return checkForServiceWorkerUpdate('deployed-version-changed');
    },
    onWatchFinished: stop,
    isVisible: isDocumentVisible,
    now: Date.now,
  });

  const checkOnReturn = (): void => void watch.checkOnReturn();
  const interval = setInterval(() => void watch.checkPeriodically(), DEPLOYED_VERSION_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', checkOnReturn);

  function stop(): void {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', checkOnReturn);
  }
  return stop;
}
